import { FC, useState, useEffect } from "react";
import { useSelector } from "react-redux";

import Modal from "components/Modal";
import { IndicatorsBtnIcon } from "../icons";
import type { IndicatorName } from "../../model";
import { selectIndicatorsCount, selectFavoriteIndicators } from "../../model";
import { IndicatorsTabs, type IndicatorsTab } from "../indicators-tabs";
import { AllIndicatorsList } from "../all-indicators-list/AllIndicatorsList";
import { CurrentIndicatorsList } from "../current-indicators-list/CurrentIndicatorsList";
import { FavoritesIndicatorsList } from "../favorites-indicators-list/FavoritesIndicatorsList";
import { DeleteAllIndicatorsButton } from "../delete-all-button/DeleteAllIndicatorsButton";
import { IndicatorSettingsModal } from "../indicator-settings-modal/IndicatorSettingsModal";

import styles from "./SelectIndicators.module.css";

interface SelectedIndicator {
  id: string;
  name: IndicatorName;
  label: string;
}

export const SelectedIndicators: FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<IndicatorsTab>("all");
  const [selectedIndicator, setSelectedIndicator] = useState<SelectedIndicator | null>(null);
  const count = useSelector(selectIndicatorsCount);
  const favorites = useSelector(selectFavoriteIndicators);
  const favoritesCount = favorites.length;

  useEffect(() => {
    if (activeTab === "current" && count === 0) setActiveTab("all");
    if (activeTab === "favorites" && favoritesCount === 0) setActiveTab("all");
  }, [activeTab, count, favoritesCount]);

  const showDeleteAll =
    count > 0 && (activeTab === "current" || activeTab === "all");

  return (
    <>
      <button
        className={`button ${styles.trigger}`}
        onClick={() => setShowModal(true)}
        title="Indicators"
      >
        <IndicatorsBtnIcon className={styles.icon} />
        {count > 0 && <span className={styles.count}>{count}</span>}
      </button>

      <Modal
        isOpen={showModal && !selectedIndicator}
        onClose={() => setShowModal(false)}
        title="Indicators"
        className={styles.modal}
      >
        <div className={styles.content}>
          {(count > 0 || favoritesCount > 0) && (
            <IndicatorsTabs
              active={activeTab}
              currentCount={count}
              favoritesCount={favoritesCount}
              onChange={setActiveTab}
            />
          )}
          {activeTab === "all" && <AllIndicatorsList />}
          {activeTab === "current" && (
            <CurrentIndicatorsList
              onIndicatorClick={(id, name, label) =>
                setSelectedIndicator({ id, name, label })
              }
            />
          )}
          {activeTab === "favorites" && <FavoritesIndicatorsList />}
          {showDeleteAll && <DeleteAllIndicatorsButton count={count} />}
        </div>
      </Modal>

      {showModal && selectedIndicator && (
        <IndicatorSettingsModal
          instanceId={selectedIndicator.id}
          indicatorName={selectedIndicator.name}
          label={selectedIndicator.label}
          onClose={() => setSelectedIndicator(null)}
        />
      )}
    </>
  );
};
